import moment from 'moment'
import { actionErrorHandler } from '@/store/utils'
import * as proofOfDeliveryService from '@/services/proof-of-delivery'
import * as transportsService from '@/services/transport-orders'
import { submitLicensePlateInfo } from '@/services/license-plate'

export const loadTransports = async ({ commit }, query) => {
  const data: any = await transportsService.fetchTransports(query)
  commit('setTransportOrders', { ...data, lastRefreshed: moment.utc().format() })
}

export const loadTransportDetails = async (store, transportId) => {
  const data = await transportsService.fetchTransportDetails(transportId)
  store.commit('setOrderDetails', data)
}
export const loadDispatchingConfirmationDetails = async (store, transportId) => {
  try {
    const data = await transportsService.fetchDispatchingConfirmationDetails(transportId)
    store.commit('setDispatchingConfirmationDetails', data)
  } catch {
    store.commit('setDispatchingConfirmationDetails', null)
  }
}

export const saveLicensePlateInfo = async (store, licensePlateInfo) => {
  await submitLicensePlateInfo(licensePlateInfo)
  store.dispatch('loadTransportDetails', licensePlateInfo.transportId)
}

export const getCanSubmitFiles = async (store, orderId) => {
  const canSubmitFiles = await proofOfDeliveryService.fetchCanSubmitFiles(orderId)
  store.commit('canSubmitFiles', canSubmitFiles)
}

export const loadPoDDocuments = actionErrorHandler((store, orderId) => {
  return proofOfDeliveryService
    .fetchPodDocuments(orderId)
    .then(documents =>
      store.commit('setPodDocuments', [...documents, ...store.getters.localPodDocuments])
    )
})

export const loadPodProcessStatus = actionErrorHandler(async (store, orderId) => {
  const processStatus = await proofOfDeliveryService.fetchPodProcessStatus(orderId)
  store.commit('setPodProcessStatus', processStatus)
})

export const addPodDocument = (store, document) => {
  store.commit('addPodDocument', document)
}

export const setPodDocumentError = (store, { localId, error = 'UPLOAD_ERROR' }) => {
  store.commit('setPodDocumentError', { localId, error })
}

export const setPodDocumentAsUploaded = (store, localId) => {
  store.commit('setPodDocumentAsUploaded', localId)
}

export const removePodDocument = actionErrorHandler(async (store, document) => {
  try {
    if (document.id) {
      store.commit('setPodDocumentAsBeingRemoved', { document, isBeingRemoved: true })
      await proofOfDeliveryService.deletePodDocument(document.id)
    }
    store.commit('removePodDocument', document)
  } catch (error) {
    store.commit('setPodDocumentAsBeingRemoved', { document, isBeingRemoved: false })
    throw error
  }
})

export const submitPodDocuments = actionErrorHandler(async (store, orderId) => {
  try {
    store.commit('setInProgressPodStepTransition', true)
    await proofOfDeliveryService.submitPodDocuments(orderId)
    await Promise.all([
      loadPoDDocuments(store, orderId),
      loadPodProcessStatus(store, orderId)
    ])
  } finally {
    store.commit('setInProgressPodStepTransition', false)
  }
})

export const loadMissingDocuments = actionErrorHandler(async (store, orderId) => {
  const missingDocuments = await proofOfDeliveryService.fetchMissingDocuments(orderId)
  store.commit('setMissingDocuments', missingDocuments)
})

export const fetchPresignedUploadRequest = async (store, { localId, orderId }) => {
  const podDocument = store.getters.getLocalPodDocument(localId)
  try {
    const presignedUploadRequest = await proofOfDeliveryService.fetchPresignedUploadRequest(
      {
        orderId,
        fileName: podDocument.fileName,
        fileSize: podDocument.fileSize
      }
    )
    return presignedUploadRequest
  } catch (error) {
    store.dispatch('setPodDocumentError', { localId })
    throw error
  }
}

export const createUploadedPodDocument = async (
  store,
  { localId, orderId, uploadRequestId }
) => {
  try {
    const podDocument = store.getters.getLocalPodDocument(localId)
    await proofOfDeliveryService.createUploadedPodDocument(
      orderId,
      uploadRequestId,
      podDocument.fileName
    )
    store.dispatch('setPodDocumentAsUploaded', localId)
  } catch (error) {
    store.dispatch('setPodDocumentError', { localId })
    throw error
  }
}

export const fetchAbleToUploadPodDocuments = async (store, orderId) => {
  const result = await proofOfDeliveryService.canUploadPodDocuments(orderId)
  store.commit('setCarrierAbleToUploadPodDocuments', result)
}
